import { DataChart, OverviewColumn, StatTile, useFormat, useOverviewQuery, WidgetCard } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation } from '@pos/i18n';
import { useMemo } from 'react';
import { hoApi } from '../api';
import { Change } from '../reports/reportFrame';
import { rangeQuery, unwrap, type ReportRange } from '../reports/reportRange';

type MembersOverview = HoComponents['schemas']['MembersOverviewDto'];

const n = (value: number | string | null | undefined) => Number(value ?? 0);
const ACCOUNTS = '/loyalty/accounts';
// Members are counted every few minutes at HO (ADR 0129); new members are read as they join.
const INTERVAL = 120_000;

function useMembersOverview(range: ReportRange) {
  return useOverviewQuery(
    ['members', range],
    () => unwrap<MembersOverview>(hoApi.GET('/api/v1/membership/overview', { params: { query: rangeQuery(range) } })),
    INTERVAL,
  );
}

/**
 * Members (ADR 0129): how many there are now and the points they hold, how many joined in the period against the period before,
 * and today. Of one store: the members who joined there.
 */
export function MemberTiles({ range }: { range: ReportRange }) {
  const { t } = useTranslation();
  const format = useFormat();
  const overview = useMembersOverview(range);
  if (overview.isError) {
    return (
      <OverviewColumn span={16}>
        <WidgetCard
          title={t('admin.overview.members.title')}
          isError
          error={overview.error}
          onRetry={() => void overview.refetch()}
        />
      </OverviewColumn>
    );
  }
  const o = overview.data;
  const joined = n(o?.newMembers);
  const before = n(o?.newMembersBefore);
  const tiles = [
    {
      key: 'members',
      label: t('admin.overview.members.members'),
      value: o ? format.number(n(o.members)) : '',
      caption: o?.asOf ? t('admin.overview.members.asOf', { time: format.time(o.asOf) }) : undefined,
    },
    {
      key: 'new',
      label: t('admin.overview.members.newMembers'),
      value: o ? format.number(joined) : '',
      // Up or down against the same number of days before (nothing to say when both are 0).
      caption:
        o && (joined !== 0 || before !== 0) ? (
          <>
            <Change now={joined} before={before} /> {t('admin.overview.biz.vsPrevious')}
          </>
        ) : undefined,
    },
    { key: 'today', label: t('admin.overview.members.newToday'), value: o ? format.number(n(o.newToday)) : '' },
    { key: 'points', label: t('admin.overview.members.pointsHeld'), value: o ? format.number(n(o.pointsHeld)) : '' },
  ];
  return (
    <>
      {tiles.map((tile) => (
        <OverviewColumn key={tile.key} span={4}>
          <StatTile
            label={tile.label}
            value={tile.value}
            caption={tile.caption}
            isLoading={overview.isPending}
            href={ACCOUNTS}
          />
        </OverviewColumn>
      ))}
    </>
  );
}

/** New members per day, this period against the one before. */
export function NewMembersTrend({ range }: { range: ReportRange }) {
  const { t } = useTranslation();
  const format = useFormat();
  const overview = useMembersOverview(range);
  const current = t('admin.overview.biz.thisPeriod');
  const previous = t('admin.overview.biz.previousPeriod');
  const { data, rows } = useMemo(() => {
    const days = overview.data?.days ?? [];
    const before = overview.data?.daysBefore ?? [];
    const label = (date: string) => format.date(date);
    return {
      data: [
        ...days.map((d) => ({ group: current, key: label(d.date), value: n(d.newMembers) })),
        ...days.flatMap((d, i) => (before[i] ? [{ group: previous, key: label(d.date), value: n(before[i].newMembers) }] : [])),
      ],
      rows: days.map((d, i) => [
        label(d.date),
        format.number(n(d.newMembers)),
        before[i] ? format.number(n(before[i].newMembers)) : '—',
      ]),
    };
  }, [current, format, overview.data, previous]);
  const joined = n(overview.data?.newMembers);
  return (
    <WidgetCard
      title={t('admin.overview.members.trend')}
      isLoading={overview.isPending}
      isError={overview.isError}
      error={overview.error}
      onRetry={() => void overview.refetch()}
      isEmpty={joined === 0 && n(overview.data?.newMembersBefore) === 0}
      emptyHeading={t('admin.overview.members.noneJoined')}
    >
      <DataChart
        kind="line"
        data={data}
        table={{ headers: [t('reports.date'), current, previous], rows }}
        summary={t('admin.overview.members.trendSummary', { count: joined, total: format.number(joined) })}
        formatValue={(v) => format.number(v)}
      />
    </WidgetCard>
  );
}

/** Members by tier now, largest first. */
export function MembersByTier({ range }: { range: ReportRange }) {
  const { t } = useTranslation();
  const format = useFormat();
  const overview = useMembersOverview(range);
  const tiers = overview.data?.tiers ?? [];
  const name = (tier: (typeof tiers)[number]) => tier.name ?? tier.code ?? t('admin.overview.members.noTier');
  return (
    <WidgetCard
      title={t('admin.overview.members.byTier')}
      isLoading={overview.isPending}
      isError={overview.isError}
      error={overview.error}
      onRetry={() => void overview.refetch()}
      isEmpty={tiers.length === 0}
      emptyHeading={t('admin.overview.members.noMembers')}
    >
      <DataChart
        kind="bar"
        data={[...tiers].reverse().map((tier) => ({ group: t('admin.overview.members.members'), key: name(tier), value: n(tier.members) }))}
        table={{
          headers: [t('admin.overview.members.tier'), t('admin.overview.members.members')],
          rows: tiers.map((tier) => [name(tier), format.number(n(tier.members))]),
        }}
        summary={t('admin.overview.members.byTierSummary', { name: tiers[0] ? name(tiers[0]) : '' })}
        formatValue={(v) => format.number(v)}
      />
    </WidgetCard>
  );
}
